import { Button } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MergeKeysService } from 'api-clients/monolith';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  makeIsMergeKeyTranslationControlEnabled,
  makeSelectWhoami,
} from 'containers/Auth_old/selectors';
import { useApiService } from 'hooks/useApiService';
import { usePostHog } from 'hooks/usePostHog';
import { useQueryState } from 'hooks/useQueryState';
import globalMessages from 'shared/global/messages';

import AddMergeKeyDialog from './AddMergeKeyDialog';
import MergeKeysTable from './MergeKeysTable';
import { messages } from './messages';

// ********************************************************************************
// == Constant ====================================================================
const DEFAULT_MERGE_KEYS_PER_PAGE = 25;
const MK_PAGE_KEY = 'mkPage';
const MK_PER_PAGE_KEY = 'mkPerPage';
const MK_QUERY_KEY = 'mkQuery';

const useStyles = makeStyles(theme => ({
  disabledMessage: {
    color: theme.palette.common.gray700,
    marginTop: theme.spacing(3),
  },
  section: {
    overflow: 'hidden',
    paddingTop: theme.spacing(3),
    position: 'relative',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  title: {
    color: theme.palette.common.gray800,
    fontWeight: 500,
  },
  instruction: {
    color: theme.palette.common.gray700,
  },
}));

// == Component ===================================================================
const MergeKeys: VFC = () => {
  const classes = useStyles();
  const mergeKeyTranslationControlEnabled = useSelector(
    makeIsMergeKeyTranslationControlEnabled(),
  );
  const posthog = usePostHog();
  const whoami = useSelector(makeSelectWhoami());

  // -- State ---------------------------------------------------------------------
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [pageRaw, setPageRaw] = useQueryState(MK_PAGE_KEY, 1);
  const [perPageRaw, setPerPageRaw] = useQueryState(
    MK_PER_PAGE_KEY,
    DEFAULT_MERGE_KEYS_PER_PAGE,
  );
  const [queryRaw, setQueryRaw] = useQueryState(MK_QUERY_KEY, '');

  const page = parseInt(pageRaw, 10) || 1;
  const perPage = parseInt(perPageRaw, 10) || DEFAULT_MERGE_KEYS_PER_PAGE;
  const query = queryRaw || '';

  // -- Callback ------------------------------------------------------------------
  const fetchCreatedMergeKeys = useCallback(
    () =>
      MergeKeysService.getInternalApiMergeKeys(
        whoami.account_id,
        query || undefined,
        page,
        perPage,
      ),
    [whoami.account_id, query, page, perPage],
  );

  const setPage = useCallback((n: number) => setPageRaw(n), [setPageRaw]);

  const setPerPage = useCallback(
    (n: number) => {
      setPerPageRaw(n);
      setPageRaw(1);
    },
    [setPageRaw, setPerPageRaw],
  );

  const setQuery = useCallback((s: string) => setQueryRaw(s), [setQueryRaw]);

  const { result, refetch } = useApiService(fetchCreatedMergeKeys, {
    enabled: !!mergeKeyTranslationControlEnabled,
  });

  // -- Effect --------------------------------------------------------------------
  useEffect(() => {
    if (!mergeKeyTranslationControlEnabled) return;

    posthog.capture('merge_keys:settings_view', {
      account_id: whoami.account_id,
      user_id: whoami.external_id,
    });
  }, [mergeKeyTranslationControlEnabled, posthog, whoami.account_id, whoami.external_id]);

  // -- Handler -------------------------------------------------------------------
  const handleAfterCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  // -- UI ------------------------------------------------------------------------
  if (!mergeKeyTranslationControlEnabled) {
    return (
      <section className={classes.section}>
        <Typography
          className={classes.disabledMessage}
          data-testid="merge-key-translation-control-disabled"
          variant="body1"
        >
          <FormattedMessage {...messages.mergeKeyTranslationControlDisabled} />
        </Typography>
      </section>
    );
  } /* else -- enabled */

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title} variant="h3">
            <FormattedMessage {...messages.panelTitle} />
          </Typography>
          <Typography className={classes.instruction} variant="body1">
            <FormattedMessage {...messages.panelInstruction} />
          </Typography>
        </div>
        <Button
          autoWidth
          onClick={() => setIsAddDialogOpen(true)}
          type="primary"
        >
          <FormattedMessage {...globalMessages.add} />
        </Button>
      </div>
      <MergeKeysTable
        accountExternalId={whoami.account_id}
        fetchResult={result}
        refetch={refetch}
        tableState={{ page, perPage, query, setPage, setPerPage, setQuery }}
      />
      <AddMergeKeyDialog
        accountExternalId={whoami.account_id}
        onClose={() => setIsAddDialogOpen(false)}
        onCreated={handleAfterCreated}
        open={isAddDialogOpen}
      />
    </section>
  );
};

// == Export ======================================================================
export default MergeKeys;
